export default function Privacy() {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '3rem 1.5rem', fontFamily: 'sans-serif', color: '#111827', lineHeight: 1.7 }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>Privacy Policy</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: '2rem' }}>Last Updated: May 13, 2026</p>
      <p>This Privacy Policy explains how we collect, use, store, and protect your information when you use our application ("Service"). It applies to all users of the Service, including physical therapists and other providers ("Providers") and patients ("Users").</p>
      <p>Please read this carefully. By using the Service, you agree to the practices described here.</p>

      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '2rem 0' }} />

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>1. Who We Are</h2>
      <p>Complete PT is a software company based in Pennsylvania. We build tools to help physical therapists manage and deliver rehabilitation programs to their patients.</p>
      <p>For questions about this policy, contact us at: <a href="mailto:support@completept.app" style={{ color: '#6366f1' }}>support@completept.app</a></p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>2. Information We Collect</h2>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>From Providers (Physical Therapists)</h3>
      <p>When you create a Provider account, we collect:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Your name and email address</li>
        <li>Your password (hashed — we cannot read it)</li>
        <li>Basic profile information you choose to provide (e.g. clinic name)</li>
      </ul>
      <p>When you use the Service, we collect:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Rehabilitation programs and exercises you create</li>
        <li>Patient accounts you create or invite</li>
        <li>Program assignments and exercise content you enter</li>
        <li>Usage data such as login times and feature interactions</li>
      </ul>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>From Patients</h3>
      <p>When you accept an invitation and create an account, we collect:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Your name and email address</li>
        <li>Your password (hashed — we cannot read it)</li>
      </ul>
      <p>When you use the Service, we collect:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Exercises assigned to you by your Provider</li>
        <li>Your exercise completion activity (which exercises you marked complete and when)</li>
        <li>Usage data such as login times</li>
      </ul>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>Automatically Collected Information</h3>
      <p>When anyone uses the Service, we automatically collect:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Device type and operating system</li>
        <li>Pages visited and time spent</li>
        <li>Error logs and performance data</li>
      </ul>
      <p>We use this information to operate, maintain, and improve the Service.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>3. Beta Phase Data Notice</h2>
      <p>The Service is currently in beta. During this phase, <strong>Providers are required not to enter real patient Protected Health Information (PHI)</strong> as defined under HIPAA. The Service is not currently operating as a HIPAA-covered Business Associate.</p>
      <p>If you are a Provider, you must use de-identified or fictional patient data only until we notify you that HIPAA-compliant infrastructure and Business Associate Agreements (BAAs) are in place.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>4. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Create and manage your account</li>
        <li>Provide the core features of the Service (program creation, assignment, and tracking)</li>
        <li>Send you emails related to your account and the Service (see Section 6)</li>
        <li>Monitor and fix bugs and performance issues</li>
        <li>Improve the Service over time</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>We do <strong>not</strong> use your information to:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Sell to third parties</li>
        <li>Serve you advertising</li>
        <li>Build profiles for marketing purposes</li>
      </ul>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>5. How We Share Your Information</h2>
      <p>We do not sell your personal information. We share it only in the following limited circumstances:</p>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>Between Providers and Patients</h3>
      <p>Providers can see the completion activity of patients they have invited. Patients can only see their own program — they cannot see other patients or any Provider account details beyond their assigned content.</p>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>Service Providers</h3>
      <p>We use a small number of third-party services to operate the platform. These providers access your information only to perform services on our behalf and are contractually required to protect it:</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 8 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Service</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Purpose</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Supabase', 'Database and authentication'],
            ['Netlify', 'Application hosting'],
            ['Resend', 'Transactional email delivery'],
            ['Sentry', 'Error monitoring and performance'],
            ['ImprovMX', 'Business email routing'],
          ].map(([service, purpose]) => (
            <tr key={service} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '8px 12px', fontWeight: 500 }}>{service}</td>
              <td style={{ padding: '8px 12px', color: '#6b7280' }}>{purpose}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1.5rem 0 6px' }}>Legal Requirements</h3>
      <p>We may disclose your information if required to do so by law, court order, or governmental authority, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</p>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '1rem 0 6px' }}>Business Transfers</h3>
      <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your information becomes subject to a different privacy policy.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>6. Email Communications</h2>
      <p>We may send you the following types of emails:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li><strong>Account emails</strong> — password resets, email verification, account changes</li>
        <li><strong>Activity emails</strong> — notifications about program assignments or updates (patients only)</li>
        <li><strong>Service emails</strong> — important updates about the Service, including changes to these policies</li>
        <li><strong>Beta feedback requests</strong> — occasional requests for feedback during the beta phase</li>
      </ul>
      <p>You can opt out of non-essential emails at any time by clicking "unsubscribe" in any email or contacting us directly. Account and security emails cannot be opted out of as they are necessary for the Service to function.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>7. Data Retention</h2>
      <p>We retain your information for as long as your account is active. If you request account deletion:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>We will delete your account and personal information within <strong>30 days</strong></li>
        <li>Anonymized or aggregated usage data that cannot identify you may be retained for analytical purposes</li>
        <li>We may retain certain information for longer if required by law</li>
      </ul>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>8. Data Security</h2>
      <p>We take reasonable technical and organizational measures to protect your information, including:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li>Encrypted data storage and transmission (TLS/SSL)</li>
        <li>Hashed passwords — we cannot read your password</li>
        <li>Access controls limiting who within our company can access user data</li>
        <li>Error monitoring to detect and respond to incidents</li>
      </ul>
      <p>No method of transmission or storage is 100% secure. If you believe your account has been compromised, contact us immediately at <a href="mailto:support@completept.app" style={{ color: '#6366f1' }}>support@completept.app</a>.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>9. Your Rights</h2>
      <p>Depending on where you live, you may have the following rights regarding your personal information:</p>
      <ul style={{ paddingLeft: '1.5rem' }}>
        <li><strong>Access</strong> — request a copy of the information we hold about you</li>
        <li><strong>Correction</strong> — request that we correct inaccurate information</li>
        <li><strong>Deletion</strong> — request that we delete your account and information</li>
        <li><strong>Portability</strong> — request your data in a machine-readable format</li>
        <li><strong>Objection</strong> — object to certain types of processing</li>
      </ul>
      <p>To exercise any of these rights, email us at <a href="mailto:support@completept.app" style={{ color: '#6366f1' }}>support@completept.app</a>. We will respond within 30 days.</p>
      <p>We do not discriminate against users who exercise their privacy rights.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>10. Children's Privacy</h2>
      <p>The Service is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If a child under 13 has been inadvertently given access to the Service, please contact us and we will delete their information promptly.</p>
      <p>For patients between the ages of 13 and 17, a parent or legal guardian must consent to their use of the Service as described in the Patient Terms.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>11. California Residents</h2>
      <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect and share, the right to delete your personal information, and the right to opt out of the sale of your personal information.</p>
      <p>We do not sell personal information as defined under the CCPA.</p>
      <p>To exercise your California privacy rights, contact us at <a href="mailto:support@completept.app" style={{ color: '#6366f1' }}>support@completept.app</a>.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>12. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email and update the "Last Updated" date at the top of this page. Your continued use of the Service after changes take effect constitutes your acceptance of the updated policy.</p>

      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '2rem 0 8px' }}>13. Contact</h2>
      <p>If you have questions, concerns, or requests related to this Privacy Policy, please contact us:</p>
      <p>
        <strong>Complete PT</strong><br />
        PO Box 106, Lyndell, PA 19354<br />
        <a href="mailto:support@completept.app" style={{ color: '#6366f1' }}>support@completept.app</a>
      </p>
    </div>
  )
}
